import React, { useState } from "react";

import {
  faFlask,
  faPlus,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { NavLink } from "react-router-dom";

import { Icon } from "src/base-components/Icon";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { useModal } from "src/design-system/Modal";
import { FeatureCatalogueWrapper } from "src/featureCatalogue/FeatureCatalogue";
import { FeatureQueryMetrics } from "src/featureCatalogue/Features/FeatureQueryMetrics";
import { Features } from "src/featureCatalogue/Features/Features";
import { ManageFeatureModal } from "src/featureCatalogue/Features/ManageFeatureModal";
import {
  ViewByType,
  ViewTypeDropdown,
} from "src/featureCatalogue/Features/ViewTypeDropdown";
import { Queries } from "src/featureCatalogue/Queries/Queries";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { useEnvironment } from "src/hooks/useEnvironment";
import { SubHeaderSearchBox } from "src/layout/SubHeader";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import {
  DashboardPageParams,
  getUrlToManageFeatureQueryPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const FeaturesHeader: React.FC<{
  onSearch: (query: string) => void;
  openCreateModal: () => void;
  currentViewOption: ViewByType;
  onCurrentViewChange: React.Dispatch<React.SetStateAction<ViewByType>>;
}> = ({
  onSearch,
  openCreateModal,
  currentViewOption,
  onCurrentViewChange,
}) => {
  const [environment, setEnvironment] = useEnvironment();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { featureCatalogue } = useCapabilities();

  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );

  const dropdownItems: OptionsDropdownElement[] = [
    {
      key: "Create Feature",
      icon: faPlus,
      action: openCreateModal,
      disabled: !featureCatalogue.canCreate
        ? "You don't have permission to create features"
        : undefined,
    },
    MENU_DIVIDER,
    {
      key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"}`,
      icon: environment === "sandbox" ? faWavePulse : faFlask,
      action: () =>
        setEnvironment(environment === "sandbox" ? "live" : "sandbox"),
    },
  ];

  return (
    <>
      {featureLifecycleEnabled && (
        <ViewTypeDropdown
          selected={currentViewOption}
          onSelect={onCurrentViewChange}
        />
      )}
      <SubHeaderSearchBox
        dataLoc="features-search"
        placeholder={
          currentViewOption === "Queries"
            ? "Search Queries..."
            : "Search Features..."
        }
        onChange={onSearch}
      />
      {featureLifecycleEnabled ? (
        <div className="flex items-center border-l p-3.5">
          <NavLink
            data-loc="features-manage-link"
            to={getUrlToManageFeatureQueryPage(orgId, wsId)}
          >
            <Icon color="text-gray-500" icon={faPlus} />
          </NavLink>
        </div>
      ) : (
        <span className="border-gray-200 border-l p-3.5">
          <EllipsisOptionsDropdown
            dataLoc="features-menu-dropdown"
            elements={dropdownItems}
            iconSize="sm"
            placement="bottom start"
          />
        </span>
      )}
    </>
  );
};

export const FeaturesPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { openModal, closeModal, isOpen, afterLeave } = useModal();
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  const [currentViewOption, setCurrentViewOption] =
    useState<ViewByType>("Features");

  useDocumentTitle(["Features"]);

  return (
    <FeatureCatalogueWrapper
      subHeaderRightContent={
        <FeaturesHeader
          currentViewOption={currentViewOption}
          openCreateModal={openModal}
          onCurrentViewChange={setCurrentViewOption}
          onSearch={setSearchQuery}
        />
      }
    >
      {featureLifecycleEnabled && <FeatureQueryMetrics />}
      <div
        className={`${featureLifecycleEnabled ? "flex min-h-0 w-full flex-1 flex-col" : ""}`}
      >
        {featureLifecycleEnabled && currentViewOption === "Queries" ? (
          <Queries searchQuery={searchQuery} />
        ) : (
          <Features openCreateModal={openModal} searchQuery={searchQuery} />
        )}
      </div>
      <ManageFeatureModal
        afterLeave={afterLeave}
        isOpen={isOpen}
        mode="create"
        onClose={closeModal}
      />
    </FeatureCatalogueWrapper>
  );
};
